/**
 * JourneySectionHeader.tsx
 *
 * Shared header for full-page journey components (fullPageComponent: true in the
 * registry). Those components own their own layout, so JourneyPage suppresses its
 * own header for them — which meant the Agreement PDF / eSign / Dealer Approval /
 * Agreement Status pages showed only a logo and no progress at all.
 *
 * This renders the grouped section bar, so the last four pages match the rest of
 * the journey. If JourneyPage didn't pass any sections (journey not configured
 * with layout.progress_section), the bar is skipped and only the title/logo row
 * renders, so nothing regresses.
 *
 * The top row is either:
 *   - a page title (+ optional right-side action) — mirrors JourneyPage's own
 *     header, which is a bold title above the progress bar; or
 *   - the Intellend logo, when no title is given (the original behaviour).
 *
 * Usage inside a full-page component:
 *   <JourneySectionHeader
 *     title="Loan Agreement Document"
 *     titleIcon={<FileText className="h-4 w-4" />}
 *     action={<button onClick={handleDownload}><Download /></button>}
 *     progressSections={progressSections}
 *     currentPageId={currentPageId}
 *   />
 */

import React from 'react';
import IntellendLogo from '@/components/IntellendLogo';
import JourneySectionProgress, { JourneyProgressSection } from '@/apps/los/components/ProgressBar/JourneySectionProgress';

interface JourneySectionHeaderProps {
  /** Passed down by JourneyPage to every fullPageComponent. */
  progressSections?: JourneyProgressSection[];
  currentPageId?: number | string | null;
  /** Page title. When set, it replaces the logo row (same look as JourneyPage). */
  title?: string;
  /** Small icon rendered before the title. */
  titleIcon?: React.ReactNode;
  /** Right-aligned control on the title row (e.g. a download button). */
  action?: React.ReactNode;
  /** Extra classes for the outer bar (e.g. `shrink-0` inside a flex column). */
  className?: string;
}

const JourneySectionHeader: React.FC<JourneySectionHeaderProps> = ({
  progressSections,
  currentPageId,
  title,
  titleIcon,
  action,
  className = '',
}) => {
  const hasSections = !!progressSections && progressSections.length > 0;

  return (
    <div className={`w-full bg-white border-b shadow-sm ${className}`}>
      <div className="mx-auto w-full max-w-md px-4 py-2">
        {title ? (
          <div className="flex items-center gap-2">
            {titleIcon && <span className="shrink-0 text-gray-700">{titleIcon}</span>}
            <h1 className="min-w-0 flex-1 truncate text-base font-bold text-gray-800">
              {title}
            </h1>
            {action && <span className="shrink-0 -mr-2">{action}</span>}
          </div>
        ) : (
          <div className="flex justify-center">
            <IntellendLogo size="sm" />
          </div>
        )}
        {hasSections && (
          <div className="mt-2">
            <JourneySectionProgress
              sections={progressSections!}
              currentPageId={currentPageId}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default JourneySectionHeader;
